import type { AgentReadCursors } from '@tunasong/schemas'

/** 90 days. Cursors older than this get pruned on every write. */
export const READ_CURSOR_PRUNE_MS = 90 * 24 * 60 * 60 * 1000

interface ClearAgentUnreadDeps {
  /** Override the current time (testing). */
  now?: () => number
}

/**
 * Compute the next `agentReadCursors` value: bump `agentId` to `now()`,
 * and drop entries older than the prune horizon.
 *
 * Pure function — the React-layer call site reads the current cursors
 * from `useUserConfig('agentReadCursors')` and writes the result back
 * through the same hook. Splitting the derivation lets us unit-test
 * the cursor algebra without happy-dom or a redux store.
 *
 * Idempotent: bumping the same agent twice produces newer-than-previous
 * cursors monotonically. Reads of equal-or-later cursor than activity
 * register as "read"; the worst case is a phantom dot that clears on
 * the next panel open.
 */
export const computeClearedCursors = (
  agentId: string,
  current: AgentReadCursors | undefined,
  deps: ClearAgentUnreadDeps = {}
): AgentReadCursors => {
  const now = deps.now ? deps.now() : Date.now()
  const horizon = now - READ_CURSOR_PRUNE_MS
  const horizonIso = new Date(horizon).toISOString()

  const next: AgentReadCursors = {}
  if (current) {
    for (const [id, cursor] of Object.entries(current)) {
      if (id === agentId) {
        // Skip the agent we're about to bump — its new value supersedes.
        continue
      }
      if (cursor >= horizonIso) {
        next[id] = cursor
      }
    }
  }
  next[agentId] = new Date(now).toISOString()
  return next
}
